import { useEffect, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import { HiXMark } from 'react-icons/hi2';
import type { ShowInfo } from '../Live';

interface ShowInfoModalProps {
  info: ShowInfo | null;
  streamImage?: string;
  onClose: () => void;
}

export function ShowInfoModal({ info, streamImage, onClose }: ShowInfoModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  const displayImage = info?.image || streamImage;

  useEffect(() => {
    if (info) {
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [info]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && info) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [info, onClose]);

  if (!info) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center
                  transition-colors duration-200
                  ${isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="show-info-title"
    >
      <FocusTrap focusTrapOptions={{ initialFocus: false, allowOutsideClick: true }}>
      <div
        className={`bg-[var(--color-surface)] rounded-t-[var(--radius-2xl)] sm:rounded-[var(--radius-2xl)]
                   shadow-[var(--shadow-xl)] w-full sm:max-w-sm max-h-[60vh] overflow-hidden
                   border border-[var(--color-border)] flex flex-col
                   transition-all duration-300 ease-out
                   ${isVisible
                     ? 'translate-y-0 opacity-100 scale-100'
                     : 'translate-y-8 opacity-0 scale-95'
                   }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with image */}
        {displayImage && (
          <div className="relative flex-shrink-0">
            <div className="h-28 overflow-hidden">
              <img
                src={displayImage}
                alt={info.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-surface)] via-transparent to-transparent" />
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-2 right-2 p-1.5
                         text-white/80 hover:text-white
                         bg-black/30 hover:bg-black/50
                         backdrop-blur-sm transition-colors rounded-full
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <HiXMark size={18} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="relative flex-1 overflow-y-auto p-4">
          {!displayImage && (
            <button
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 p-1.5
                         text-[var(--color-text-muted)] hover:text-[var(--color-text)]
                         transition-colors rounded-full
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            >
              <HiXMark size={18} />
            </button>
          )}
          <h2
            id="show-info-title"
            className="text-base font-semibold text-[var(--color-text)] leading-tight pr-6"
          >
            {info.title}
          </h2>
          {info.description ? (
            <p className="mt-3 text-sm text-[var(--color-text-secondary)] leading-relaxed">
              {info.description}
            </p>
          ) : (
            <p className="mt-3 text-sm text-[var(--color-text-muted)] italic">
              No description available
            </p>
          )}
        </div>
      </div>
      </FocusTrap>
    </div>
  );
}
